import {
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  InformationCircleIcon,
  UserIcon,
} from '@heroicons/react/20/solid';
import { ExternalLink } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import {
  CompanionTaskSummary,
  useGetTaskCompanionTasksQuery,
} from '@/services/api/tasksService';
import { CanonicalTaskStatus } from '@/services/types/task-types';

interface CompanionTasksTabProps {
  matterId: string;
  taskId: string;
}

const STATUS_STYLES: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  [CanonicalTaskStatus.COMPLETED]: {
    bg: 'bg-green-100 dark:bg-green-900/50',
    text: 'text-green-700 dark:text-green-300',
    label: 'Done',
  },
  [CanonicalTaskStatus.IN_PROGRESS]: {
    bg: 'bg-blue-100 dark:bg-blue-900/50',
    text: 'text-blue-700 dark:text-blue-300',
    label: 'In Progress',
  },
  [CanonicalTaskStatus.PENDING]: {
    bg: 'bg-zinc-100 dark:bg-zinc-800',
    text: 'text-zinc-600 dark:text-zinc-400',
    label: 'Pending',
  },
  [CanonicalTaskStatus.BLOCKED]: {
    bg: 'bg-amber-100 dark:bg-amber-900/50',
    text: 'text-amber-700 dark:text-amber-300',
    label: 'Blocked',
  },
  [CanonicalTaskStatus.NOT_APPLICABLE]: {
    bg: 'bg-zinc-100 dark:bg-zinc-800',
    text: 'text-zinc-500 dark:text-zinc-500',
    label: 'N/A',
  },
  [CanonicalTaskStatus.CLOSED]: {
    bg: 'bg-zinc-100 dark:bg-zinc-800',
    text: 'text-zinc-500 dark:text-zinc-500',
    label: 'Closed',
  },
};

/** Active statuses that represent work in progress */
const ACTIVE_STATUSES = new Set([
  CanonicalTaskStatus.PENDING,
  CanonicalTaskStatus.IN_PROGRESS,
  CanonicalTaskStatus.BLOCKED,
]);

/** Format date as short string (e.g., "Jan 15") */
const formatShortDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Group tasks by matter_id */
interface MatterGroup {
  matter_id: string;
  matter_name: string;
  is_primary: boolean;
  tasks: CompanionTaskSummary[];
}

const groupTasksByMatter = (
  tasks: CompanionTaskSummary[],
  showActiveOnly: boolean,
): MatterGroup[] => {
  const groups = tasks.reduce<Map<string, MatterGroup>>((acc, task) => {
    const existing = acc.get(task.matter_id);
    if (existing) {
      // Only add if it's a real task (not a placeholder with null task_id)
      if (task.task_id) {
        // Filter by active status if showActiveOnly is true
        if (
          !showActiveOnly ||
          ACTIVE_STATUSES.has(task.task_status as CanonicalTaskStatus)
        ) {
          existing.tasks.push(task);
        }
      }
    } else {
      const shouldIncludeTask =
        task.task_id &&
        (!showActiveOnly ||
          ACTIVE_STATUSES.has(task.task_status as CanonicalTaskStatus));
      acc.set(task.matter_id, {
        matter_id: task.matter_id,
        matter_name: task.matter_name,
        is_primary: task.is_primary,
        // Empty array if no matching task (placeholder entry)
        tasks: shouldIncludeTask ? [task] : [],
      });
    }
    return acc;
  }, new Map());

  // Sort matters alphabetically by name
  return Array.from(groups.values()).sort((a, b) =>
    a.matter_name.localeCompare(b.matter_name),
  );
};

/** Single task row within a matter group */
const TaskRow: React.FC<{ task: CompanionTaskSummary }> = ({ task }) => {
  const statusStyle =
    STATUS_STYLES[task.task_status ?? ''] ?? STATUS_STYLES.PENDING;

  return (
    <div className="flex items-center justify-between py-1.5 px-2 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {/* Task name - takes available space */}
        <span
          className="text-[11px] text-zinc-600 dark:text-zinc-400 truncate flex-1 min-w-0"
          title={task.task_name ?? undefined}
        >
          {task.task_name}
        </span>

        {/* Assignee - more space for name */}
        {task.assignee_name && (
          <div
            className="flex items-center gap-0.5 text-[10px] text-zinc-400 dark:text-zinc-500 flex-shrink-0"
            title={task.assignee_name}
          >
            <UserIcon className="h-2.5 w-2.5 flex-shrink-0" />
            <span className="truncate max-w-[100px]">{task.assignee_name}</span>
          </div>
        )}

        {/* Due date */}
        {task.task_due_date && (
          <div className="flex items-center gap-0.5 text-[10px] text-zinc-400 dark:text-zinc-500 flex-shrink-0">
            <CalendarIcon className="h-2.5 w-2.5" />
            <span>{formatShortDate(task.task_due_date)}</span>
          </div>
        )}
      </div>

      {/* Status badge + task link */}
      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
        <span
          className={`text-[9px] font-medium px-1 py-0.5 rounded ${statusStyle.bg} ${statusStyle.text}`}
        >
          {statusStyle.label}
        </span>
        <Link
          to={`/matters/${task.matter_id}/?task_id=${task.task_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-zinc-400 dark:text-zinc-500 hover:text-blue-500"
          title="Open task"
        >
          <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
};

/** Collapsible matter group with tasks */
const MatterGroupCard: React.FC<{ group: MatterGroup }> = ({ group }) => {
  const taskCount = group.tasks.length;
  const hasTasks = taskCount > 0;

  return (
    <Collapsible defaultOpen>
      <div className="rounded-md border border-zinc-200 dark:border-zinc-800 overflow-hidden">
        {/* Header - always visible */}
        <CollapsibleTrigger
          className={cn(
            'flex items-center justify-between w-full px-2 py-1.5',
            'bg-zinc-50 dark:bg-zinc-900/50',
            'hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors',
          )}
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-xs font-medium text-zinc-900 dark:text-white truncate">
              {group.matter_name}
            </span>
            {group.is_primary && (
              <Badge
                variant="secondary"
                className="text-[8px] px-1 py-0 h-3.5 bg-zinc-200 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400 flex-shrink-0"
              >
                Primary
              </Badge>
            )}
            {hasTasks && (
              <span className="text-[10px] text-zinc-400 dark:text-zinc-500 flex-shrink-0">
                ({taskCount} {taskCount === 1 ? 'task' : 'tasks'})
              </span>
            )}
          </div>
          <ChevronDownIcon className="h-3.5 w-3.5 text-zinc-400 transition-transform [[data-state=open]>&]:rotate-180" />
        </CollapsibleTrigger>

        {/* Content - tasks list */}
        <CollapsibleContent>
          <div className="divide-y divide-zinc-100 dark:divide-zinc-800 bg-white dark:bg-zinc-900">
            {hasTasks ? (
              group.tasks.map((task) => (
                <TaskRow key={task.task_id} task={task} />
              ))
            ) : (
              <div className="py-2 px-2 text-[10px] text-zinc-400 dark:text-zinc-500 italic">
                No matching tasks
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

/**
 * CompanionTasksTab - Shows tasks of the same group across companion cases.
 *
 * Displayed in the task detail drawer when the task belongs to a companion case group.
 * Allows users to see the status of related tasks (e.g., "Medical Records" group) across
 * all family members in the same accident case.
 *
 * Tasks are grouped by matter for a compact, scannable display.
 */
export const CompanionTasksTab: React.FC<CompanionTasksTabProps> = ({
  matterId,
  taskId,
}) => {
  const [showActiveOnly, setShowActiveOnly] = React.useState(false);
  const { data, isLoading, error } = useGetTaskCompanionTasksQuery({
    matterId,
    taskId,
  });

  const matterGroups = React.useMemo(() => {
    const tasks = data?.companion_tasks ?? [];
    return groupTasksByMatter(tasks, showActiveOnly);
  }, [data?.companion_tasks, showActiveOnly]);

  // Count total tasks to decide whether to show the filter toggle
  const totalTasks = React.useMemo(() => {
    const tasks = data?.companion_tasks ?? [];
    return tasks.filter((t) => t.task_id).length;
  }, [data?.companion_tasks]);

  if (error) {
    return (
      <div className="text-xs text-red-600 dark:text-red-400 py-4 px-2">
        Failed to load companion tasks
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="animate-pulse text-zinc-400 dark:text-zinc-500 text-xs">
          Loading...
        </div>
      </div>
    );
  }

  if (matterGroups.length === 0 && !showActiveOnly) {
    return (
      <div className="py-6 flex flex-col items-center justify-center">
        <div className="size-8 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mb-1">
          <InformationCircleIcon className="size-4 text-zinc-400 dark:text-zinc-500" />
        </div>
        <p className="text-xs font-medium text-zinc-400 dark:text-zinc-500">
          No companion cases
        </p>
      </div>
    );
  }

  return (
    <div className="px-2 pt-1.5 pb-2">
      {/* Filter toggle - aligned right, compact */}
      {totalTasks > 0 && (
        <div className="flex items-center justify-end pb-1.5">
          <button
            type="button"
            onClick={() => setShowActiveOnly(!showActiveOnly)}
            className={cn(
              'text-[10px] font-medium px-2 py-1 rounded border transition-colors flex items-center gap-1',
              showActiveOnly
                ? 'bg-blue-50 dark:bg-blue-900/50 border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-300'
                : 'bg-zinc-50 dark:bg-zinc-800/50 border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800',
            )}
          >
            <span
              className={cn(
                'flex items-center justify-center w-3 h-3 rounded-sm border transition-colors',
                showActiveOnly
                  ? 'bg-blue-500 dark:bg-blue-600 border-blue-500 dark:border-blue-600'
                  : 'border-zinc-300 dark:border-zinc-600',
              )}
            >
              {showActiveOnly && <CheckIcon className="w-2.5 h-2.5 text-white" />}
            </span>
            Active Only
          </button>
        </div>
      )}

      {/* Matter groups */}
      <div className="space-y-2">
        {matterGroups.length > 0 ? (
          matterGroups.map((group) => (
            <MatterGroupCard key={group.matter_id} group={group} />
          ))
        ) : (
          <div className="py-4 text-center text-[11px] text-zinc-400 dark:text-zinc-500 italic">
            No active tasks found
          </div>
        )}
      </div>
    </div>
  );
};
